import { useCallback, useEffect, useMemo } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useCatalogDataContext,
  useCatalogDataContextEventsSelector,
} from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkEditMarketplaceSeatingDialog } from 'src/dialogs/BulkEdits/BulkEditMarketplaceSeatingDialog/BulkEditMarketplaceSeatingDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { getSharedVenueConfigId } from 'src/utils/venueConfigUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Event,
  ListingDetails,
  ListingDetailsUpdateInput,
  ListingQuery,
  Permission,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_MARKETPLACE_SEATING_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchUpdateMarketplaceSeating } from './LaunchEditMarketplaceSeating';

export const useBulkEditMarketplaceSeatingAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_UpdateListingInfo,
    Permission.Inventory_UpdateListingInfoOnIsPricerOf,
    Permission.Inventory_UpdateListingInfoOnPurchasedBy
  );
  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `marketplace-seating-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_MARKETPLACE_SEATING_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const catalogEvents = useCatalogDataContextEventsSelector();
  // A single shared venue config lets the dialog offer StubHub section/row
  // dropdowns instead of free-text (POS-9305 FR5).
  const sharedVenueConfigId = useMemo(
    () =>
      event
        ? (event.venCfgId ?? null)
        : getSharedVenueConfigId(
            catalogEvents,
            filterQueryWithEventIds.eventOrMappingIds
          ),
    [catalogEvents, event, filterQueryWithEventIds.eventOrMappingIds]
  );
  const { showErrorDialog } = useClientLogContext();

  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();

  const {
    openDialog: openBulkEditMarketplaceSeatingDialog,
    closeDialog: closeBulkEditMarketplaceSeatingDialog,
  } = useDialog(
    DialogId.BulkEditMarketplaceSeating,
    BulkEditMarketplaceSeatingDialog
  );
  const onSubmit = useCallback(
    async (
      listingUpdate: ListingDetailsUpdateInput | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateMarketplaceSeatingOverridesPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog(
              'bulkUpdateMarketplaceSeatingOverridesPreview',
              error,
              {
                trackErrorData: { filterQueryWithEventIds },
              }
            );
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (listingUpdate) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateMarketplaceSeatingOverrides(
              {
                item1: preview!.preview,
                item2: listingUpdate,
              },
              updateKey,
              supportBackgroundProcess,
              // Existing flow passes explicit row overrides; don't auto-resolve rows.
              false
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }
              closeBulkEditMarketplaceSeatingDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateMarketplaceSeatingOverrides', error, {
              trackErrorData: { listingUpdate, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkEditMarketplaceSeatingDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    openBulkEditMarketplaceSeatingDialog({
      updateKey,
      sharedVenueConfigId,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditMarketplaceSeatingDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditMarketplaceSeating,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditMarketplaceSeating,
        disabled: !hasPermission,
      });
    };
  }, [hasPermission, openDialogStableRef, registerCommand, unregisterCommand]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkEditMarketplaceSeatingDialog({
        updateKey,
        sharedVenueConfigId,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditMarketplaceSeatingDialog,
      });
    },
    [
      closeBulkEditMarketplaceSeatingDialog,
      event,
      onSubmit,
      openBulkEditMarketplaceSeatingDialog,
      setIsLoading,
      setListItemExpansion,
      sharedVenueConfigId,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPermission) {
      return [];
    }
    return [
      {
        title: ContentId.UpdateMarketplaceSeating,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [hasPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchUpdateMarketplaceSeating
        key="MarketplaceSeating"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
